import { css } from '@emotion/react';
import { Link } from '@mui/material';
import { styled } from '@mui/system';
import { useAtomValue } from 'jotai';
import { useMemo, useState } from 'react';
import { FieldPath, FieldValues, FormProvider, useForm } from 'react-hook-form';
import { PermissionGatedWrapper } from '../../../components/Fallback/PermissionGatedWrapper';
import { FormFactoryNoLayout } from '../../../components/Form/FormFactory';
import { ICompanyDataModel } from '../../../data-models/company.data-model';
import { PermissionKey } from '../../../services/PermissionAndRolesKeys';
import { companyMetricsAtomJ } from '../../../services/state/CompanyMetricsStateJ';
import { companyStateJ } from '../../../services/state/CompanyStateJ';
import { FDMap } from '../../../util/data-structure/FDMap';
import { createForm, IFormField } from '../../../view-models/form.view-model';
import { selectedDetailsFieldsOrgState } from '../../AdminPanel/DataDisplay/DataDisplayState';
import { UnstyledFieldset } from '../../CompanyProfiles/Scenarios/components/commonStyledComponents';
import {
  CompanyDetailsPanelViewModel,
  fromCompanyDetailsVM,
  toCompanyDetailsVM,
} from './CompanyDetailsPanel.view-model';
import { PrimaryForbiddenMessage } from './constants/forbidden-messages.constants';
import { useShowSummaryV2 } from './useShowSummaryV2';
import { Panel } from './Panel';
import { SummarySection } from './SummarySection';
import { withDashPlaceholder } from './withDashPlaceholder';
import { useCompanyDetailsFields } from './useCompanyDetailsFields';
import { useDebounceCompanyUpdates } from './useDebounceCompanyUpdates';
import { useCompanyEditPerms } from './useEditPerms';

export const CompanyDetailsPanelBreakpoint = '380px';

export const FieldsWrapper = styled('div')`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  grid-template-rows: repeat(4, minmax(4rem, auto));
  gap: 0 2rem;
  min-height: 320px;
  @container fd-panel (width < ${CompanyDetailsPanelBreakpoint}) {
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: auto;
    min-height: 0;
  }
`;

/**
 * The `showSummaryV2` Company Details grid: four columns of fields, per design. Local to this card
 * rather than a change to `FieldsWrapper`, which is shared with the Digest, both Fund Profiles
 * panels and three Deal Drawer panels — none of which are being redesigned.
 *
 * Rows are implicit so a partial last row does not leave empty tracks behind, and the column count
 * steps down as the card narrows: the card is ~996px wide at the design's 1440px canvas, but the
 * layout collapses to a single column below the container breakpoint.
 */
const fieldsWrapperV2Css = css`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  grid-auto-rows: minmax(4rem, auto);
  gap: 0 2rem;
  @container fd-panel (width < 780px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  @container fd-panel (width < ${CompanyDetailsPanelBreakpoint}) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

export const FieldsPerPage = 8;

export interface IProfilePanelProps<T extends FieldValues> {
  companyId: number;
  fieldsToShow?: FieldPath<T>[];
  editable?: boolean;
  defaultPage?: number;
}

export function CompanyDetailsPanel({
  companyId,
  fieldsToShow,
  editable = true,
  defaultPage,
}: IProfilePanelProps<ICompanyDataModel>) {
  const showSummaryV2 = useShowSummaryV2();
  const selectedFields = useAtomValue(selectedDetailsFieldsOrgState);
  const setEditableFields = useCompanyEditPerms(companyId);

  const companyDetailsFields = useCompanyDetailsFields(fieldsToShow ?? selectedFields);
  const fields = useMemo(
    () => setEditableFields(companyDetailsFields),
    [companyDetailsFields, setEditableFields]
  );
  // V1 paginates. V2 shows one page plus a "Show All (N)" link that reveals the rest in place —
  // `defaultPage` still selects which page that is, because the Admin Panel's field-order config
  // (CompanyDetailsConfig) renders this component once per page to preview what each one holds.
  const { paginationConfig, visibleFields } = usePanelPagination({
    fields,
    initialPage: defaultPage,
  });
  const [showAll, setShowAll] = useState(false);
  const v2Fields = showAll ? fields : visibleFields;
  const isTruncated = fields.length > FieldsPerPage;

  return (
    <PermissionGatedWrapper
      permissions={[PermissionKey.canViewCompanyBasics, PermissionKey.canViewMetrics]}
      requireAll={true}
      fallbackMessage={PrimaryForbiddenMessage}
    >
      {showSummaryV2 ? (
        <SummarySection title={'Company Details'} scrollBody={false}>
          <UnstyledFieldset disabled={!editable}>
            <CompanyDetailsPage
              fields={withDashPlaceholder(v2Fields)}
              companyId={companyId}
              wideGrid
            />
          </UnstyledFieldset>
          {isTruncated && (
            <Link
              color={'secondary'}
              component='button'
              underline='hover'
              onClick={() => setShowAll((prev) => !prev)}
              sx={{ justifySelf: 'start', marginTop: '0.5rem' }}
            >
              {showAll ? 'Show Less' : `Show All (${fields.length})`}
            </Link>
          )}
        </SummarySection>
      ) : (
        <Panel
          title={'Company Details'}
          paginationConfig={paginationConfig}
          showContentOverflow={true}
          defaultPage={defaultPage}
        >
          <UnstyledFieldset disabled={!editable}>
            <CompanyDetailsPage fields={visibleFields} companyId={companyId} />
          </UnstyledFieldset>
        </Panel>
      )}
    </PermissionGatedWrapper>
  );
}

interface ICompanyDetailsProps {
  fields: IFormField<unknown>[];
  companyId: number;
  /** V2 lays the fields out in four columns rather than two. */
  wideGrid?: boolean;
}
function CompanyDetailsPage(props: ICompanyDetailsProps) {
  const { fields, companyId, wideGrid } = props;
  const company = useAtomValue(companyStateJ(companyId))!;
  const companyMetrics = useAtomValue(companyMetricsAtomJ({ companyId }));

  const methods = useForm({
    values: toCompanyDetailsVM(company, companyMetrics),
  });

  const updateCompany = useDebounceCompanyUpdates(companyId);
  const onChange = (data: Partial<CompanyDetailsPanelViewModel>) => {
    return updateCompany(fromCompanyDetailsVM(data));
  };

  const form = createForm<CompanyDetailsPanelViewModel>({
    fields: FDMap.fromArray(fields, 'key'),
    variant: 'form-inline',
  });

  if (wideGrid) {
    return (
      <FormProvider {...methods}>
        <div css={fieldsWrapperV2Css}>
          <FormFactoryNoLayout form={form} onChange={onChange} />
        </div>
      </FormProvider>
    );
  }

  return (
    <FormProvider {...methods}>
      <FieldsWrapper>
        <FormFactoryNoLayout form={form} onChange={onChange} />
      </FieldsWrapper>
    </FormProvider>
  );
}

interface IPaginationConfig {
  paginationConfig: {
    onChange: React.Dispatch<React.SetStateAction<number>>;
    pageCount: number;
  };
}

export function usePanelPagination<T>({
  fields,
  itemsPerPage = FieldsPerPage,
  initialPage = 1,
}: {
  fields: T[];
  itemsPerPage?: number;
  initialPage?: number;
}): IPaginationConfig & { visibleFields: T[] } {
  const [currentPage, setCurrentPage] = useState(initialPage);
  const visibleFields = fields.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  return {
    paginationConfig: {
      onChange: setCurrentPage,
      pageCount: Math.ceil(fields.length / itemsPerPage),
    },
    visibleFields,
  };
}
